import { Component, computed, signal } from '@angular/core';
import { SynIconComponent } from '@synergy-design-system/angular';
import { AuthUser, getUserData } from '../../api/auth';

interface DocLink {
  text: string;
  href: string;
}

@Component({
  selector: 'app-dashboard',
  template: `
    <div class="container">
      <div class="welcome">
        <h2>
          <span i18n="@@hello">Hello</span>
          <span>{{ userFullName() }}</span>
          <span>!</span>
        </h2>

        <h1 i18n="@@welcome-to-the-portal">
          Welcome to the SICK ID Self-Service Portal!
        </h1>
      </div>
      <div class="feedback">
        <div class="feedback-box flex-col">
          <div class="flex-row gap-1">
            <syn-icon name="feedback_fill" />
            <strong style="margin-top: auto" class="big-font">
              <span i18n="@@questions-and-feedback">Questions and Feedback</span>
            </strong>
          </div>
          <div class="margin-top-1">
          <span class="medium-font" i18n="@@feedback-description">
            Get in touch with us when you need help or want additional features.
            We are happy to hear from you!
          </span>
          </div>
          <div class="flex-row margin-top-1">
            <a style="margin-left: auto" class="white-button-link" [href]="contactUrl()">
              <span i18n="@@contact-us">Contact Us</span>
            </a>
          </div>
        </div>
      </div>
      <div class="docs">
        <div class="flex-row gap-1">
          <syn-icon name="help_fill" />
          <strong style="margin-top: auto" class="big-font">
            <span i18n="@@links-and-documentation">Links and Documentation</span>
          </strong>
        </div>

        <div class="flex-row margin-top-1" style="gap: 6rem">
          <div class="flex-col gap-1">
            <h2>SICK ID</h2>
            @for (link of generalLinks; track link.text) {
              <a class="syn-link"
                 [href]="link.href">
                {{ link.text }}
              </a>
            }
          </div>

          <div class="flex-col gap-1">
            <h2>Service Portal</h2>
            @for (link of documentation; track link.text) {
              <a class="syn-link"
                 [href]="link.href">
                {{ link.text }}
              </a>
            }
          </div>

          <div class="flex-col gap-1">
            <h2>How Tos</h2>
            @for (link of howTos; track link.text) {
              <a class="syn-link"
                 [href]="link.href">
                {{ link.text }}
              </a>
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .container {
        display: grid;
        grid-template-areas:
          "logo    feedback"
          "welcome feedback"
          "docs    docs";
        grid-template-columns: 1fr 3fr;
        gap: 2rem;
        padding: 5px;
      }

      .welcome {
        grid-area: welcome;
        min-width: 320px;
      }

      .feedback {
        grid-area: feedback;
      }

      .feedback-box {
        margin-left: auto;
        background-color: var(--syn-color-primary-600);
        color: white;
        border-radius: 5px;
        padding: 1rem;
        width: 400px;
      }

      .docs {
        grid-area: docs;
        border-radius: 5px;
        border: 1px solid var(--syn-color-neutral-300);
        padding: 1rem;
      }

      syn-icon {
        font-size: var(--syn-font-size-2x-large);
      }

      .medium-font {
        font-size: var(--syn-font-size-medium);
      }

      .big-font {
        font-size: var(--syn-font-size-large);
      }

      .white-button-link {
        color: white;
        padding: 1rem;
        border: 1px solid white;
        border-radius: var(--syn-button-border-radius-medium);
        text-decoration: none;
        font-weight: bold;
      }

      .syn-link {
        font-size: var(--syn-font-size-medium);
      }
    `,
  ],
  imports: [SynIconComponent],
})
export class DashboardComponent {

  user = signal<AuthUser>(getUserData());

  userFullName = computed(() => {
    const user = this.user();
    const firstName = user.givenName ?? '';
    const lastName = user.familyName ?? '';
    return ' ' + firstName + ' ' + lastName;
  });

  generalLinks: DocLink[] = [
    {
      text: 'SICK ID Offerings',
      href: 'https://mosaicplus.sick.com/x/mZVXaQ',
    },
    {
      text: 'SICK ID Blog',
      href: 'https://mosaicplus.sick.com/pages/viewrecentblogposts.action?key=SICKID',
    },
    {
      text: 'SICK ID F.A.Q.',
      href: 'https://mosaicplus.sick.com/x/GOtlC',
    },
    {
      text: 'SICK ID Support',
      href: 'https://mosaicplus.sick.com/x/RAgRBw',
    },
  ];

  howTos: DocLink[] = [
    {
      text: 'How to Manage OpenID Clients',
      href: 'https://mosaicplus.sick.com/x/z5OXeg',
    },
    {
      text: 'How to Choose the Right SICK ID Stage',
      href: 'https://mosaicplus.sick.com/x/IZOXeg',
    },
    {
      text: 'How to Start Securing your Digital Service',
      href: 'https://mosaicplus.sick.com/x/4HvyeQ',
    },
    {
      text: 'How to Handle SICK ID Tokens',
      href: 'https://mosaicplus.sick.com/x/RurdeQ',
    },
    {
      text: 'How to Implement Service-to-Service Authentication',
      href: 'https://mosaicplus.sick.com/x/xfj4eQ',
    },
    {
      text: 'How to Link to SICK ID Services',
      href: 'https://mosaicplus.sick.com/x/v5GXeg',
    },
    {
      text: 'How to Create and Manage Test User Accounts',
      href: 'https://mosaicplus.sick.com/x/74yXeg',
    },
  ];

  documentation: DocLink[] = [
    {
      text: 'Service Portal',
      href: 'https://mosaicplus.sick.com/x/JhRglw',
    },
    {
      text: 'Customer Manager',
      href: 'https://mosaicplus.sick.com/x/FBhglw',
    },
    {
      text: 'Client Manager',
      href: 'https://mosaicplus.sick.com/x/HRhglw',
    },
    {
      text: 'Test User Manager',
      href: 'https://mosaicplus.sick.com/x/Ixhglw',
    },
  ];


  contactUrl() {
    const recipient = 'sick-id-service@sick.de';
    const subject = '[SICK ID Service Portal] Feedback';
    return `mailto:${recipient}?subject=${encodeURIComponent(subject)}`;
  }
}
